import React from 'react';
import flatpickr from 'flatpickr';
import type { Instance } from 'flatpickr/dist/types/instance';
import { Dutch } from 'flatpickr/dist/l10n/nl.js';
import 'flatpickr/dist/flatpickr.min.css';
import { getDefaultLocale } from './seachpage-helpers';

// Derive a flatpickr token format (e.g. "d-m-Y" for nl, "m/d/Y" for en-US) from the configured
// culture using Intl, so the visible input matches the app's culture for ANY locale — unlike the
// native <input type="date">, whose displayed format is locked to the browser/OS locale.
function localeDateFormat(locale: string): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date(2023, 0, 2));
    return parts
      .map(p => (p.type === 'day' ? 'd' : p.type === 'month' ? 'm' : p.type === 'year' ? 'Y' : p.value))
      .join('');
  } catch {
    return 'd-m-Y';
  }
}

interface DateInputProps {
  /** ISO yyyy-MM-dd (any time component is stripped). */
  value: string;
  /** Emits the selected date as ISO yyyy-MM-dd (empty string when cleared). */
  onChange: (isoDate: string) => void;
  className?: string;
  placeholder?: string;
}

/**
 * Date picker that stores/emits ISO (yyyy-MM-dd) while displaying the date in the app's configured
 * culture (getDefaultLocale()). Replaces the native date input, which follows the browser locale.
 */
export function DateInput({ value, onChange, className, placeholder }: DateInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const fpRef = React.useRef<Instance | null>(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const isoDate = (value || '').split('T')[0];

  // Initialize once.
  React.useEffect(() => {
    if (!inputRef.current) return;
    const locale = getDefaultLocale();
    const fp = flatpickr(inputRef.current, {
      dateFormat: 'Y-m-d',                 // underlying value stays ISO
      altInput: true,                      // visible field shows the culture-formatted date
      altFormat: localeDateFormat(locale),
      altInputClass: className || '',
      allowInput: true,
      ...(locale.toLowerCase().startsWith('nl') ? { locale: Dutch } : {}),
      onChange: (_dates, dateStr) => onChangeRef.current(dateStr),
    }) as Instance;
    if (placeholder && fp.altInput) fp.altInput.placeholder = placeholder;
    if (isoDate) fp.setDate(isoDate, false, 'Y-m-d');
    fpRef.current = fp;
    return () => { fp.destroy(); fpRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the picker in sync when the value changes from outside.
  React.useEffect(() => {
    const fp = fpRef.current;
    if (!fp) return;
    const current = fp.selectedDates[0] ? fp.formatDate(fp.selectedDates[0], 'Y-m-d') : '';
    if (isoDate !== current) {
      if (isoDate) fp.setDate(isoDate, false, 'Y-m-d'); else fp.clear(false);
    }
  }, [isoDate]);

  return <input ref={inputRef} type="text" />;
}
